import { AppLayOut } from "components/app-layout";
import { ErrorBoundary } from "components/error-boundary";
import { LoadingSpinner } from "components/loading-spinner";
import PermissionProtectedRoute from "components/protected-route/permission-protected-route.ts";
import ProtectedRoutes from "components/protected-route/protected-routes";
import { SnackBar } from "components/snack-bar";
import TranslationGate from "components/translation-gate";
import { ROUTES } from "libs";
import { RolesTypes } from "libs/models/login";
import { CROSSPAY_ROUTES } from "libs/routes";
import { CardOperationsContainer } from "pages/CardMerchantOperations";
import { lazy, Suspense, useEffect } from "react";
import { connect } from "react-redux";
import { Navigate, Route, Routes } from "react-router";
import "./App.css";
import { AppProps } from "./App.props";
import { ApiProgressIndicatorContainer } from "./components/api-progress-indicator/api-progress-indicator-container";
import { ModalPopUpContainer } from "./components/modal/modal-popup.container";
import { CookiesKeys } from "./libs/enums";
import { AppDispatch, ApplicationState } from "./store";
import { removeSnack } from "./store/slices/ui";
import { setCookie } from "./utils/cookies";

// Lazy load all page components
const DashboardContainer = lazy(() =>
  import("pages/Dashboard").then((module) => ({
    default: module.DashboardContainer,
  })),
);
const SignInContainer = lazy(() =>
  import("pages/Signin").then((module) => ({
    default: module.SignInContainer,
  })),
);
const ForgetPasswordContainer = lazy(() =>
  import("pages/ForgetPassword").then((module) => ({
    default: module.ForgetPasswordContainer,
  })),
);
const CheckBalanceContainer = lazy(() =>
  import("pages/CheckBalance").then((module) => ({
    default: module.CheckBalanceContainer,
  })),
);
const InvoiceDetailsContainer = lazy(() =>
  import("pages/InvoiceDetailsView").then((module) => ({
    default: module.InvoiceDetailsContainer,
  })),
);
const ProductItemDetailsContainer = lazy(() =>
  import("pages/ProductItemDetailsView").then((module) => ({
    default: module.ProductItemDetailsContainer,
  })),
);
const BatchDetailsContainer = lazy(() =>
  import("pages/BatchDetailsView").then((module) => ({
    default: module.BatchDetailsContainer,
  })),
);
const PointOfSaleContainer = lazy(() =>
  import("pages/PointOfSale").then((module) => ({
    default: module.PointOfSaleContainer,
  })),
);
const InvoicesViewContainer = lazy(() =>
  import("pages/InvoicesView").then((module) => ({
    default: module.InvoicesViewContainer,
  })),
);
const RedemptionChannelContainer = lazy(() =>
  import("pages/RedemptionChannelView").then((module) => ({
    default: module.RedemptionChannelContainer,
  })),
);
const ProductViewContainer = lazy(() =>
  import("pages/ProductsView").then((module) => ({
    default: module.ProductViewContainer,
  })),
);
const ProductDetailsContainer = lazy(() =>
  import("pages/ProductDetailsView").then((module) => ({
    default: module.ProductDetailsContainer,
  })),
);
const UserViewContainer = lazy(() =>
  import("pages/UserView").then((module) => ({
    default: module.UserViewContainer,
  })),
);
const SettingsContainer = lazy(() =>
  import("pages/Settings").then((module) => ({
    default: module.SettingsContainer,
  })),
);
const ChangePasswordContainer = lazy(() =>
  import("pages/ChangePassword").then((module) => ({
    default: module.ChangePasswordContainer,
  })),
);
const EditPointOfSaleContainer = lazy(() =>
  import("pages/EditPointOfSale").then((module) => ({
    default: module.EditPointOfSaleContainer,
  })),
);
const SalesReportContainer = lazy(() =>
  import("pages/SalesReport").then((module) => ({
    default: module.SalesReportContainer,
  })),
);
const ProductInventoryContainer = lazy(() =>
  import("pages/ProductInventory").then((module) => ({
    default: module.ProductInventoryContainer,
  })),
);
const TransactionReportContainer = lazy(() =>
  import("pages/TransactionsReport").then((module) => ({
    default: module.TransactionReportContainer,
  })),
);
const ClientPageContainer = lazy(() =>
  import("pages/ClientView").then((module) => ({
    default: module.ClientPageContainer,
  })),
);
const AddClientContainer = lazy(() =>
  import("pages/AddClient").then((module) => ({
    default: module.AddClientContainer,
  })),
);
const EditClientContainer = lazy(() =>
  import("pages/EditClient").then((module) => ({
    default: module.EditClientContainer,
  })),
);
const ClientDetailsContainer = lazy(() =>
  import("pages/ClientDetails").then((module) => ({
    default: module.ClientDetailsContainer,
  })),
);
const AddPointOfSaleContainer = lazy(() =>
  import("pages/AddPointOfSale").then((module) => ({
    default: module.AddPointOfSaleContainer,
  })),
);
const AddProductContainer = lazy(() =>
  import("pages/AddProduct/add-product.container").then((module) => ({
    default: module.AddProductContainer,
  })),
);
const EditProductContainer = lazy(() =>
  import("pages/EditProduct").then((module) => ({
    default: module.EditProductContainer,
  })),
);
const AddRedemptionChannelContainer = lazy(() =>
  import("pages/AddRedemptionChannel").then((module) => ({
    default: module.AddRedemptionChannelContainer,
  })),
);
const EditRedemptionChannelContainer = lazy(() =>
  import("pages/EditRedemptionChannel").then((module) => ({
    default: module.EditRedemptionChannelContainer,
  })),
);
const AddUserContainer = lazy(() =>
  import("pages/AddUser/add-user.container").then((module) => ({
    default: module.AddUserContainer,
  })),
);
const EditUserContainer = lazy(() =>
  import("pages/EditUser").then((module) => ({
    default: module.EditUserContainer,
  })),
);

const App = (props: AppProps) => {
  const dir = props.appLocale === "ar" ? "rtl" : "ltr";
  useEffect(() => {
    setCookie(CookiesKeys.userCulture, props.appLocale);
    // Keep localStorage in sync for translation utility
    try {
      localStorage.setItem("language", props.appLocale);
    } catch {}
    document.documentElement.setAttribute("dir", dir);
  }, [props.appLocale]);
  return (
    <ErrorBoundary>
      <TranslationGate>
        <div className="h-full w-full">
          {/* API Progress Indicator - shows automatically during API requests */}
          <ApiProgressIndicatorContainer
            variant="circular"
            position="fixed"
            color="primary"
          />
          <ModalPopUpContainer />
          <Suspense
            fallback={
              <LoadingSpinner size="large" message="Loading application..." />
            }
          >
            <Routes>
              <Route path={ROUTES.LOGIN} element={<SignInContainer />} />
              <Route
                path={ROUTES.FORGET_PASSWORD}
                element={<ForgetPasswordContainer />}
              />

              <Route
                path={ROUTES.BASE}
                element={
                  <ProtectedRoutes>
                    <AppLayOut />
                  </ProtectedRoutes>
                }
              >
                <Route
                  path={CROSSPAY_ROUTES.DASHBOARD}
                  element={<DashboardContainer />}
                />
                <Route
                  path={CROSSPAY_ROUTES.CHECK_BALANCE}
                  element={<CheckBalanceContainer />}
                />
                <Route
                  path={CROSSPAY_ROUTES.CARD_OPERATIONS}
                  element={<CardOperationsContainer />}
                />
                <Route
                  path={`${CROSSPAY_ROUTES.INVOICE_DETAILS}/:id`}
                  element={<InvoiceDetailsContainer />}
                />
                <Route
                  path={`${CROSSPAY_ROUTES.PRODUCT_ITEM_VIEW}/:barcode`}
                  element={<ProductItemDetailsContainer />}
                />
                <Route
                  path={`${CROSSPAY_ROUTES.BATCH_DETAILS_VIEW}/:id`}
                  element={<BatchDetailsContainer />}
                />
                <Route
                  path={CROSSPAY_ROUTES.POINT_OF_SALE}
                  element={<PointOfSaleContainer />}
                />
                <Route
                  path={CROSSPAY_ROUTES.INVOICING}
                  element={<InvoicesViewContainer />}
                />
                <Route
                  path={CROSSPAY_ROUTES.REDEMPTION_CHANNEL}
                  element={<RedemptionChannelContainer />}
                />
                <Route
                  path={CROSSPAY_ROUTES.PRODUCT}
                  element={<ProductViewContainer />}
                />
                <Route
                  path={`${CROSSPAY_ROUTES.PRODUCT_DETAILS}/:id`}
                  element={<ProductDetailsContainer />}
                />
                <Route
                  path={CROSSPAY_ROUTES.USER_VIEW}
                  element={<UserViewContainer />}
                />
                <Route
                  path={CROSSPAY_ROUTES.SETTINGS}
                  element={<SettingsContainer />}
                />
                <Route
                  path={CROSSPAY_ROUTES.CHANGE_PASSWORD}
                  element={<ChangePasswordContainer />}
                />
                <Route
                  path={`${CROSSPAY_ROUTES.POINT_OF_SALE}/:id`}
                  element={<EditPointOfSaleContainer />}
                />
                <Route path={CROSSPAY_ROUTES.REPORTS}>
                  <Route
                    path={CROSSPAY_ROUTES.REPORT_SALES}
                    element={<SalesReportContainer />}
                  />
                  <Route
                    path={CROSSPAY_ROUTES.REPORT_PRODUCT_INVENTORY}
                    element={<ProductInventoryContainer />}
                  />
                  <Route
                    path={CROSSPAY_ROUTES.REPORT_TRANSACTION}
                    element={<TransactionReportContainer />}
                  />
                </Route>
                <Route
                  element={
                    <PermissionProtectedRoute
                      premission={RolesTypes.SuperAdmin}
                    />
                  }
                >
                  <Route
                    path={CROSSPAY_ROUTES.CLIENT_VIEW}
                    element={<ClientPageContainer />}
                  />
                  <Route
                    path={CROSSPAY_ROUTES.ADD_CLIENT}
                    element={<AddClientContainer />}
                  />
                  <Route
                    path={`${CROSSPAY_ROUTES.EDIT_CLIENT}/:id`}
                    element={<EditClientContainer />}
                  />
                  <Route
                    path={`${CROSSPAY_ROUTES.CLIENT_DETAILS}/:id`}
                    element={<ClientDetailsContainer />}
                  />

                  <Route
                    path={CROSSPAY_ROUTES.ADD_POINT_OF_SALE}
                    element={<AddPointOfSaleContainer />}
                  />
                  <Route
                    path={CROSSPAY_ROUTES.ADD_PRODUCT}
                    element={<AddProductContainer />}
                  />
                  <Route
                    path={`${CROSSPAY_ROUTES.EDIT_PRODUCT}/:id`}
                    element={<EditProductContainer />}
                  />
                  <Route
                    path={CROSSPAY_ROUTES.ADD_BRANCH}
                    element={<AddRedemptionChannelContainer />}
                  />
                  <Route
                    path={`${CROSSPAY_ROUTES.EDIT_BRANCH}/:id`}
                    element={<EditRedemptionChannelContainer />}
                  />
                  <Route
                    path={CROSSPAY_ROUTES.ADD_USER}
                    element={<AddUserContainer />}
                  />
                  <Route
                    path={`${CROSSPAY_ROUTES.EDIT_USER}/:id`}
                    element={<EditUserContainer />}
                  />
                </Route>
              </Route>
              <Route
                path="*"
                element={<Navigate to={ROUTES.LOGIN} replace />}
              />
            </Routes>
          </Suspense>
          <SnackBar snack={props.snack} onRemoveSnack={props.onRemoveSnack} />
        </div>
      </TranslationGate>
    </ErrorBoundary>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onRemoveSnack: () => {
    dispatch(removeSnack());
  },
});

const mapStateToProps = (state: ApplicationState) => {
  return {
    loggedInUserRole: state.login.userRole,
    userCulture: state.login.userCulture,
    appLocale: state.ui.locale,
    isPasswordExpired: state.login.isPasswordExpired,
    snack:
      Object.keys(state.ui.snacks).length > 0
        ? state.ui.snacks[Object.keys(state.ui.snacks)[0]]
        : undefined,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
